import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { brand } from '../theme/tokens';
import { brandAssets } from '../theme/brand-assets';
import { useLanguage } from './language-context';
import { NavIcon } from './nav-icon';
import { navGroups, navItemFor } from './nav-items';
import { LanguageToggle } from './language-toggle';
import { ThemeToggle } from './theme-toggle';
import { NotificationTray } from './notification-tray';
import { AccountMenu } from './account-menu';

// The sticky top bar (design M:161-246; CB-17 `top-bar.tsx`). Desktop: page icon
// tile + kicker (the rail group, "Dashboard" on Home) + page title; the ⌘K search
// pill; language and theme toggles; the bell + help cluster; the user menu. Mobile:
// the brand lockup, a search button, bell and avatar (language/theme move into the
// user menu). ⌘K / Ctrl+K opens the palette from anywhere.
export function TopBar({
  onSearch,
  notifOpen,
  onNotifOpenChange,
  menuOpen,
  onMenuOpenChange,
  isStaff,
  onHelp,
  onContact,
  onLogout,
  loggingOut,
}: {
  onSearch: () => void;
  notifOpen: boolean;
  onNotifOpenChange: (open: boolean) => void;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  isStaff: boolean;
  onHelp: () => void;
  onContact: () => void;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const item = navItemFor(pathname);
  const kicker =
    item && item.to !== '/' ? navGroups.find((g) => g.group === item.group)?.label : undefined;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onSearch();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSearch]);

  return (
    <header className="top-bar">
      <span className="top-bar__brand">
        <img className="top-bar__logo" src={brandAssets.lockup} alt={brand.name} />
        <img className="top-bar__logo top-bar__logo--rev" src={brandAssets.lockupReversed} alt={brand.name} />
      </span>
      <div className="top-bar__page">
        <span className="top-bar__page-icon">
          <NavIcon name={item?.icon ?? 'home'} size={19} strokeWidth={1.9} />
        </span>
        <span className="top-bar__page-text">
          <span className="top-bar__kicker">{t(kicker ?? 'dc70.group.dashboard')}</span>
          <h2 className="top-bar__title">{item ? t(item.key) : ''}</h2>
        </span>
      </div>

      <button
        type="button"
        className="top-bar__search"
        onClick={onSearch}
        aria-label={t('dc70.top.search_open')}
        data-shell="search"
      >
        <NavIcon name="search" size={18} strokeWidth={2} />
        <span className="top-bar__search-text">{t('dc70.top.search')}</span>
        <kbd className="top-bar__kbd">⌘K</kbd>
      </button>

      <div className="top-bar__end">
        <span className="top-bar__prefs">
          <LanguageToggle />
          <ThemeToggle />
        </span>
        <div className="top-bar__cluster">
          <NotificationTray open={notifOpen} onOpenChange={onNotifOpenChange} />
          <span className="top-bar__rule" aria-hidden="true" />
          <button
            type="button"
            className="top-bar__help"
            aria-label={t('dc70.top.help')}
            onClick={onHelp}
          >
            ?
          </button>
        </div>
        <AccountMenu
          open={menuOpen}
          onOpenChange={onMenuOpenChange}
          isStaff={isStaff}
          onHelp={onHelp}
          onContact={onContact}
          onLogout={onLogout}
          loggingOut={loggingOut}
        />
      </div>
    </header>
  );
}
